/**
 * result-v2/ui/linkPreview.ts
 * ---------------------------------------------------------------------------
 * Live Open Graph preview for a bare profile URL — the equivalent of the old
 * UI's `component/searcher/LinkPreview.tsx`, which dispatched
 * `GET_LINK_PREVIEW` to hit `GET /searcher/url?url=` (a backend proxy that
 * scrapes the target page for og:title / og:description / og:image) and
 * rendered whatever came back.
 *
 * `LinkPreviewCard` (ui/primitives.tsx) deliberately does NOT fetch anything
 * — it only renders data a provider already returned. This hook is for the
 * cases where nothing but a bare link exists (e.g. NameScanTemplate's
 * Candidate profiles built from a flat PLUTO record's `FacebookId`, which
 * carries no photo or title of its own) and the old UI's answer to that gap
 * was exactly this live fetch.
 *
 * Reuses the same `getLinkPreview` action/saga/`/searcher/url` endpoint the
 * old component used, but fixes the two bugs that component had:
 *  - No caching: every mount of the same URL refired the request. Here a
 *    module-level cache is keyed by URL and shared across every card, so
 *    the same profile link anywhere on the page only ever gets fetched once.
 *  - No error recovery: the saga's `catch` never invokes the callback, so a
 *    failed fetch left the old component's spinner spinning forever. Here a
 *    client-side timeout flips a hung/failed request to an `error` state so
 *    the caller can fall back to a plain link instead.
 */
import * as React from 'react'
// Admin copy: the admin store has no getLinkPreview saga, so this calls the
// same GET /searcher/url endpoint directly.
import { api } from '../../../function/axios'

export type LinkPreviewData = {
  success?: boolean
  url?: string
  title?: string
  siteName?: string
  description?: string
  images?: string[]
  favicons?: string[]
}

type Status = 'idle' | 'loading' | 'done' | 'error'
type CacheEntry = { status: Status; data?: LinkPreviewData }
type PreviewResult = { status: Status; data?: LinkPreviewData }

const cache = new Map<string, CacheEntry>()
const listeners = new Map<string, Set<() => void>>()
const TIMEOUT_MS = 12000

function notify(key: string) {
  listeners.get(key)?.forEach((fn) => fn())
}

/**
 * VK's mobile domain never returns OG tags for the scraper — the old
 * component silently rewrote it to the desktop domain before fetching.
 * Kept here for parity so VK profile links still resolve a preview.
 */
function normalize(url: string): string {
  return url.replace(/^https:\/\/m\.vk\.com/, 'https://vk.com')
}

/**
 * Fires the proxy fetch for `key` at most once (module-level cache dedupes
 * repeat calls — from the same card re-rendering, from a sibling card
 * pointing at the same URL, or from a parent that's also watching this key
 * to sort by description length) and notifies every subscriber when it
 * settles.
 */
function ensureFetch(key: string) {
  if (cache.has(key)) return
  cache.set(key, { status: 'loading' })
  let settled = false
  const timer = setTimeout(() => {
    if (settled) return
    settled = true
    cache.set(key, { status: 'error' })
    notify(key)
  }, TIMEOUT_MS)

  api({ method: 'GET', url: `/searcher/url?url=${key}` })
    .then(({ data: ogData }) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      const ok = ogData && ogData.success !== false
      cache.set(key, { status: ok ? 'done' : 'error', data: ok ? ogData : undefined })
      notify(key)
    })
    .catch(() => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      cache.set(key, { status: 'error' })
      notify(key)
    })
}

/**
 * Fetches (and subscribes to) previews for a whole list of URLs at once —
 * for callers that need every result together, e.g. PersonTemplate's
 * Websites card sorting cards by description length before it decides
 * which get a full-width row. `undefined` entries pass through as `idle`
 * so a caller can keep a stable, positionally-matched array without
 * filtering first.
 */
export function useLinkPreviews(urls: (string | undefined)[]): PreviewResult[] {
  const [, forceRender] = React.useReducer((n: number) => n + 1, 0)
  const keys = urls.map((u) => (u ? normalize(u) : undefined))
  const signature = keys.join('|')

  React.useEffect(() => {
    keys.forEach((key) => { if (key) ensureFetch(key) })
    // Re-fetch only when the actual set of URLs changes, not on every
    // render or dispatch-identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature])

  React.useEffect(() => {
    const unique = Array.from(new Set(keys.filter(Boolean))) as string[]
    unique.forEach((key) => {
      const existing = listeners.get(key)
      const set = existing ?? new Set<() => void>()
      if (!existing) listeners.set(key, set)
      set.add(forceRender)
    })
    return () => { unique.forEach((key) => listeners.get(key)?.delete(forceRender)) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature])

  return keys.map((key) => (key ? cache.get(key) ?? { status: 'idle' } : { status: 'idle' }))
}

export function useLinkPreview(url: string | undefined): PreviewResult {
  return useLinkPreviews([url])[0]
}
